import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { adminFetch } from '../utils/adminFetch';
import { getAdminCourseName } from '../utils/courseDisplay';
import StudentProfileCard, { StudentProfileInfo } from './StudentProfileCard';

type TrackingProfile = {
  student: StudentProfileInfo;
  course: {
    id: string;
    title: string | Record<string, string>;
    courseProject?: string;
    defaultLanguage?: string;
  };
  group: { id: string; name: string } | null;
  enrollmentDate: string | null;
};

const StudentTrackingShell: React.FC = () => {
  const { courseId, studentId } = useParams<{ courseId: string; studentId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

  const activeTab = location.pathname.endsWith('/messages') ? 'messages' : 'detail';

  const [profile, setProfile] = useState<TrackingProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!courseId || !studentId) return;
    void (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await adminFetch(
          `${apiBase}/student-messages/courses/${courseId}/students/${studentId}/history`
        );
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data?.error || data?.message || 'Impossible de charger le profil apprenant');
        }
        if (!data.student) {
          throw new Error('Profil apprenant introuvable');
        }
        setProfile({
          student: data.student,
          course: data.course || { id: courseId, title: 'Formation' },
          group: data.group || null,
          enrollmentDate: data.enrollmentDate || null,
        });
      } catch (err: any) {
        setProfile(null);
        setError(err.message || 'Erreur de chargement');
      } finally {
        setLoading(false);
      }
    })();
  }, [apiBase, courseId, studentId]);

  const handleTabChange = (_: React.SyntheticEvent, value: string) => {
    if (!courseId || !studentId) return;
    if (value === 'messages') {
      navigate(`/courses/${courseId}/students/${studentId}/messages`);
    } else {
      navigate(`/courses/${courseId}/students/${studentId}/detail`);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(`/courses/${courseId}/students`)}
        sx={{ mb: 2 }}
      >
        Retour aux apprenants
      </Button>

      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && profile && (
        <>
          <Typography variant="h4" fontWeight={700} sx={{ mb: 3 }}>
            Formation : {getAdminCourseName(profile.course)}
          </Typography>

          <StudentProfileCard
            student={profile.student}
            groupName={profile.group?.name}
            enrollmentDate={profile.enrollmentDate}
            courseDefaultLanguage={profile.course.defaultLanguage}
          />

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={activeTab} onChange={handleTabChange} aria-label="Suivi apprenant">
              <Tab value="detail" label="Suivi détaillé" />
              <Tab value="messages" label="Messages & parcours" />
            </Tabs>
          </Box>

          <Outlet />
        </>
      )}
    </Container>
  );
};

export default StudentTrackingShell;
